import { invoke } from '@tauri-apps/api/core'

export interface HandsSidecarHealth {
  type: 'hands.health'
  schema_version: number
  status: 'ready'
  client_version: string
  protocol_version: number
}

export interface HandsCompatibilityRetry {
  compatible: boolean
  attempts: number
  health: HandsSidecarHealth | null
  failureCode: string | null
}

export type HandsRuntimePhase =
  | 'stopped'
  | 'starting'
  | 'running'
  | 'backoff'
  | 'blocked'
  | 'locked_out'

export interface HandsActiveAction {
  commandId: string
  actionType:
    | 'browser_navigate'
    | 'browser_read_page'
    | 'browser_snapshot'
    | 'browser_scroll'
    | 'browser_click'
    | 'browser_fill'
    | 'native_observe'
    | 'native_set_value'
    | 'native_press'
    | 'fs_list'
    | 'fs_read_text'
    | 'fs_write_text'
    | 'vault_list_markdown'
    | 'vault_read_markdown'
    | 'vault_publish_markdown'
  status: 'started' | 'cancelling'
  startedAt: number
}

export interface HandsRuntimeStatus {
  status: HandsRuntimePhase
  desiredRunning: boolean
  restartCount: number
  failureCode: string | null
  clientVersion: string | null
  protocolVersion: number | null
  localActionsEnabled: boolean
  activeAction: HandsActiveAction | null
}

export interface HandsAuthority {
  configured: boolean
  paused: boolean
  grantRevision: number
  browserRead: boolean
  browserControl: boolean
  workspaceRead: boolean
  workspaceWrite: boolean
  workspaceName: string | null
  resourceGrants: LocalResourceGrant[]
}

export interface LocalResourceGrant {
  id: string
  resourceType: 'markdown_vault' | 'native_application'
  label: string
  read: boolean
  publish: boolean
  applicationId?: string
  edit?: boolean
  press?: boolean
}

export const nativeApplicationsSupported = async (): Promise<boolean> => (
  (await invoke<{ localApplications: boolean }>('client_capabilities')).localApplications
)
export const nativeAccessibilityStatus = (): Promise<boolean> => invoke('native_accessibility_status')
export const chooseNativeApplication = (): Promise<HandsAuthority> => invoke('choose_native_application')
export const setNativeApplicationAccess = (
  grantId: string, edit: boolean, press: boolean, remove = false,
): Promise<HandsAuthority> => invoke('set_native_application_access', { grantId, edit, press, remove })

export interface DesktopUpdateCandidate {
  version: string
  releaseEpoch: number
  notes: string
  critical: boolean
  recovery: boolean
  sidecarVersion: string
}

export interface DesktopUpdateStatus {
  configured: boolean
  channel: string
  currentVersion: string
  trustedReleaseEpoch: number
  update: DesktopUpdateCandidate | null
}

export interface DesktopDiagnosticReport {
  schemaVersion: number
  generatedAtUnixSeconds: number
  application: {
    version: string
    operatingSystem: string
    architecture: string
  }
  sessionStatus: 'active' | 'signed_out' | 'unavailable'
  cache: {
    status: 'not_initialized' | 'ready' | 'empty' | 'unavailable' | 'purge_failed' | 'quarantined'
    encrypted: true
    schemaVersion: number | null
    resourceStoreSchemaVersion: number | null
    cursorSchemaVersion: number | null
    mutationJournalSchemaVersion: number | null
    mutationEntrySchemaVersion: number | null
    resourceCount: number
    pendingMutationCount: number
    pendingOperationDraftCount?: number
    pendingOperationAttentionDraftCount?: number
    migrationStatus: 'not_initialized' | 'current' | 'migrated' | 'blocked'
    syncStatus: 'not_synchronized' | 'partial' | 'synchronized'
    serverVersion: number
    synchronizedAtUnixSeconds: number | null
    authorizationLeaseStatus: 'not_issued' | 'reauthorization_required' | 'active' | 'expired' | 'clock_invalid' | 'unavailable'
    authorizationLeaseExpiresAtUnixSeconds: number | null
    cacheReadsAuthorized: boolean
  }
  sidecar: {
    status: 'compatible' | 'unavailable'
    runtimeStatus: HandsRuntimePhase
    desiredRunning: boolean
    restartCount: number
    failureCode: string | null
    runtimeFailureCode: string | null
    clientVersion: string | null
    protocolVersion: number | null
    localActionsEnabled: boolean
  }
  updater: {
    configurationStatus: 'development' | 'ready' | 'invalid'
    channel: string
    trustStatus: 'not_configured' | 'ready' | 'unavailable'
    trustedReleaseEpoch: number | null
  }
  privacy: {
    secretsIncluded: false
    personalDataIncluded: false
    rawLogsIncluded: false
    localPathsIncluded: false
  }
}

export const probeHandsSidecar = async (): Promise<HandsSidecarHealth> => (
  invoke<HandsSidecarHealth>('probe_hands_sidecar')
)

export const retryHandsCompatibilityCheck = async (): Promise<HandsCompatibilityRetry> => (
  invoke<HandsCompatibilityRetry>('retry_hands_compatibility_check')
)

export const getHandsRuntimeStatus = async (): Promise<HandsRuntimeStatus> => (
  invoke<HandsRuntimeStatus>('hands_runtime_status')
)

export const startHandsRuntime = async (): Promise<void> => {
  await invoke('start_hands_runtime')
}

export const stopHandsRuntime = async (): Promise<void> => {
  await invoke('stop_hands_runtime')
}

export const restartHandsRuntime = async (): Promise<void> => {
  await invoke('restart_hands_runtime')
}

export const cancelHandsAction = async (commandId: string): Promise<void> => {
  await invoke('cancel_hands_action', { commandId })
}

export const getHandsAuthority = async (): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('hands_authority_status')
)

export const enableHandsAuthority = async (): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('enable_hands_authority')
)

export const setHandsPaused = async (paused: boolean): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('set_hands_paused', { paused })
)

export const setHandsBrowserControl = async (
  enabled: boolean,
): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('set_hands_browser_control', { enabled })
)

export const chooseMarkdownVault = async (allowPublish: boolean): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('choose_markdown_vault', { allowPublish })
)

export const removeMarkdownVault = async (grantId: string): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('remove_markdown_vault', { grantId })
)

export const setMarkdownVaultPublication = async (
  grantId: string,
  allowPublish: boolean,
): Promise<HandsAuthority> => (
  invoke<HandsAuthority>('set_markdown_vault_publication', { grantId, allowPublish })
)

export const collectDesktopDiagnostics = async (): Promise<DesktopDiagnosticReport> => (
  invoke<DesktopDiagnosticReport>('collect_desktop_diagnostics')
)

export const getDesktopCacheStatus = async (): Promise<DesktopDiagnosticReport['cache']> => (
  invoke<DesktopDiagnosticReport['cache']>('desktop_cache_status')
)

export interface DesktopCacheSyncReport {
  pagesApplied: number
  resourceCount: number
  serverVersion: number
  synchronizedAtUnixSeconds: number
  operationDraftsSubmitted: number
  operationDraftsHeld: number
  pendingOperationDrafts: number
  operationAttentionDraftsSubmitted?: number
  operationAttentionDraftsHeld?: number
  pendingOperationAttentionDrafts?: number
}

export interface DesktopOperationDraftInput {
  idempotencyKey: string
  title: string
  objective: string
  mode: 'run' | 'monitor'
  definitionOfDone: string[]
  customerGuardrails: string | null
}

export interface DesktopPendingOperationDraft {
  id: string
  title: string
  objective: string
  createdAtUnixSeconds: number
  expiresAtUnixSeconds: number
  status: 'queued' | 'needs_review'
}

export type DesktopOperationAttentionReason =
  | 'approval'
  | 'missing_context'
  | 'correction'
  | 'permission'
  | 'connection'
  | 'sign_in'
  | 'policy_block'
  | 'budget'
  | 'route_unavailable'
  | 'uncertain_result'
  | 'recovery'

export interface DesktopOperationAttentionDraftInput {
  idempotencyKey: string
  operationId: string
  baseVersion: number
  reason: DesktopOperationAttentionReason
  summary: string
}

export interface DesktopPendingOperationAttentionDraft {
  idempotencyKey: string
  operationId: string
  baseVersion: number
  reason: DesktopOperationAttentionReason
  summary: string
  createdAtUnixSeconds: number
  expiresAtUnixSeconds: number
  status: 'queued' | 'needs_review'
  reviewReason: 'version_conflict' | 'cloud_refused' | null
}

export interface DesktopCachedCommercialOperation {
  id: string
  title: string
  objective: string
  operationType: string
  mode: 'run' | 'monitor'
  phase: 'draft' | 'planning' | 'ready' | 'active' | 'monitoring' | 'verifying' | 'completed' | 'failed' | 'cancelled'
  attentionRequired: boolean
  attentionReason: string | null
  resultClassification: 'verified' | 'provider_accepted' | 'user_confirmed' | 'partially_verified' | 'unverified' | 'failed' | 'denied' | 'cancelled' | null
  resultSummary: string | null
  definitionOfDone: string[]
  deadlineAt: string | null
  monitoringUntil: string | null
  version: number
  lastMeaningfulActivityAt: string
  completedAt: string | null
  createdAt: string
}

export interface DesktopCachedCommercialOperationEvent {
  id: string
  operationId: string
  operationVersion: number
  eventType: string
  riskClass: string
  summary: string
  createdAt: string
}

export interface DesktopCachedProjection {
  operations: DesktopCachedCommercialOperation[]
  activity: DesktopCachedCommercialOperationEvent[]
  serverVersion: number
  synchronizedAtUnixSeconds: number
  authorizationLeaseExpiresAtUnixSeconds: number
  pendingOperationDrafts: DesktopPendingOperationDraft[]
  pendingOperationAttentionDrafts?: DesktopPendingOperationAttentionDraft[]
  authorizationLeaseRemainingMilliseconds: number
  authorizationCheckedAtUnixMilliseconds: number
}

export const synchronizeDesktopCache = async (): Promise<DesktopCacheSyncReport> => (
  invoke<DesktopCacheSyncReport>('synchronize_desktop_cache')
)

export const readDesktopCachedProjection = async (): Promise<DesktopCachedProjection> => (
  invoke<DesktopCachedProjection>('read_desktop_cached_projection')
)

export const queueDesktopOperationDraft = async (
  input: DesktopOperationDraftInput,
): Promise<DesktopPendingOperationDraft> => (
  invoke<DesktopPendingOperationDraft>('queue_desktop_operation_draft', { input })
)

export const discardDesktopOperationDraft = async (operationId: string): Promise<void> => {
  await invoke('discard_desktop_operation_draft', { operationId })
}

export const queueDesktopOperationAttentionDraft = async (
  input: DesktopOperationAttentionDraftInput,
): Promise<DesktopPendingOperationAttentionDraft> => (
  invoke<DesktopPendingOperationAttentionDraft>(
    'queue_desktop_operation_attention_draft',
    { input },
  )
)

export const discardDesktopOperationAttentionDraft = async (
  idempotencyKey: string,
): Promise<void> => {
  await invoke('discard_desktop_operation_attention_draft', { idempotencyKey })
}

export const openLinkedInAuthorization = async (authorizationUrl: string): Promise<void> => {
  await invoke('open_linkedin_authorization', { authorizationUrl })
}

export const openLinkedInSignIn = async (): Promise<void> => {
  await invoke('open_linkedin_sign_in')
}

export const checkDesktopUpdate = async (): Promise<DesktopUpdateStatus> => (
  invoke<DesktopUpdateStatus>('check_desktop_update')
)

export const installDesktopUpdate = async (
  version: string,
  releaseEpoch: number,
): Promise<void> => {
  await invoke('install_desktop_update', {
    expectedVersion: version,
    expectedReleaseEpoch: releaseEpoch,
  })
}
